import {useMutation} from '@tanstack/react-query';
import {useNavigate} from 'react-router';
import pick from 'lodash/pick';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {getReportUrl} from 'common/report/util';
import {useTranslation} from 'common/i18n/hooks';
import {useContentSidenavContext} from 'common/widgets/content-sidenav/context';
import {SidenavSectionIndex, SidenavSectionTrackingName} from 'common/widgets/content-sidenav/constants';
import {parseFetchError} from 'common/util/shared-client';

import {useExperimentPageContext} from '../context';
import {ListPageTrackingEvent, trackExperimentListPageAction} from '../tracking';
import {useStagedExperiment} from './staged-experiment';

export function useCreateExperiment() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {stagedExperiment} = useStagedExperiment();
  const {projectId, workspaceId} = useExperimentPageContext();
  const contentSidenavContext = useContentSidenavContext();

  const {createExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });

  const createMutation = useMutation({
    mutationFn: createExperiment,
    onSuccess: (response) => {
      contentSidenavContext?.insertContentSidenavItem(
        response,
        SidenavSectionIndex.Yours,
        SidenavSectionTrackingName.Yours,
      );
      navigate(
        getReportUrl(`experiments/${response.id.toString()}`, {
          projectId,
          workspaceId,
        }),
        {
          replace: true,
        },
      );
    },
    onError: (err) => {
      const {statusCode, message} = parseFetchError(err);

      const isLimitError = statusCode === 403 && message?.includes(`has reached the limit`);

      addToast({
        message: t(isLimitError ? `experiments.actions.create.fail-limit` : `experiments.actions.create.fail`),
        type: ToastType.ERROR,
        timeout: 6000,
      });
    },
    onSettled: (_, error) => {
      trackExperimentListPageAction({
        event: error ? ListPageTrackingEvent.CreateExperimentError : ListPageTrackingEvent.CreateExperiment,
        props: error ? {'error message': error.message} : {},
      });
    },
  });

  const create = () => {
    createMutation.mutate({
      name: stagedExperiment.name,
      hypothesis: stagedExperiment.hypothesis,
      settings: pick(stagedExperiment.settings, [
        `collectionMethod`,
        `controlKey`,
        `featureFlagSettings`,
        `exposureEventSettings`,
      ]),
    });
  };

  return create;
}
